import React, { useState, useEffect, useMemo } from 'react';
import './ProjectSummaryDetails.css';

const ProjectSummaryDetails = ({ projectAllocationData }) => {
  const [summaryData, setSummaryData] = useState([]);
  const [sortField, setSortField] = useState('projectName');
  const [sortDirection, setSortDirection] = useState('asc');

  // Experience level categories
  const EXPERIENCE_LEVELS = {
    JUNIOR: { min: 0, max: 24, label: 'Junior (0-2 yrs)', score: 100, color: '#4CAF50' },
    MID: { min: 24, max: 60, label: 'Mid (2-5 yrs)', score: 70, color: '#FF9800' },
    SENIOR: { min: 60, max: 999, label: 'Senior (5+ yrs)', score: 40, color: '#F44336' }
  };

  // Calculate experience level for a resource
  const getExperienceLevel = (experienceInMonths) => {
    const exp = parseInt(experienceInMonths) || 0;
    if (exp <= 24) return 'JUNIOR';
    if (exp <= 60) return 'MID';
    return 'SENIOR';
  };

  // Calculate profitability score based on resource mix
  const calculateProfitabilityScore = (resources) => {
    if (!resources || resources.length === 0) return 0;
    
    let totalScore = 0;
    let totalAllocation = 0;
    
    resources.forEach(resource => {
      const experience = parseInt(resource.totalExperience) || 0;
      const allocation = parseFloat(resource.billAllocation) || 0;
      const level = getExperienceLevel(experience);
      const score = EXPERIENCE_LEVELS[level].score;
      
      totalScore += (score * allocation);
      totalAllocation += allocation;
    });
    
    return totalAllocation > 0 ? Math.round(totalScore / totalAllocation) : 0;
  };

  // Get profitability category and color
  const getProfitabilityCategory = (score) => {
    if (score >= 80) return { category: 'High', color: '#4CAF50', trend: '↑' };
    if (score >= 60) return { category: 'Moderate', color: '#FF9800', trend: '→' };
    return { category: 'Low', color: '#F44336', trend: '↓' };
  };

  // Calculate experience distribution for a project
  const calculateExperienceDistribution = (resources) => {
    const distribution = {
      JUNIOR: { count: 0, allocation: 0 },
      MID: { count: 0, allocation: 0 },
      SENIOR: { count: 0, allocation: 0 }
    };

    resources.forEach(resource => {
      const level = getExperienceLevel(resource.totalExperience);
      const allocation = parseFloat(resource.billAllocation) || 0;
      
      distribution[level].count++;
      distribution[level].allocation += allocation;
    });

    const totalResources = resources.length;
    const totalAllocation = Object.values(distribution).reduce((sum, d) => sum + d.allocation, 0);

    return {
      junior: {
        count: distribution.JUNIOR.count,
        percentage: totalResources > 0 ? Math.round((distribution.JUNIOR.count / totalResources) * 100) : 0,
        allocation: Math.round(distribution.JUNIOR.allocation),
        allocationPercentage: totalAllocation > 0 ? Math.round((distribution.JUNIOR.allocation / totalAllocation) * 100) : 0
      },
      mid: {
        count: distribution.MID.count,
        percentage: totalResources > 0 ? Math.round((distribution.MID.count / totalResources) * 100) : 0,
        allocation: Math.round(distribution.MID.allocation),
        allocationPercentage: totalAllocation > 0 ? Math.round((distribution.MID.allocation / totalAllocation) * 100) : 0
      },
      senior: {
        count: distribution.SENIOR.count,
        percentage: totalResources > 0 ? Math.round((distribution.SENIOR.count / totalResources) * 100) : 0,
        allocation: Math.round(distribution.SENIOR.allocation),
        allocationPercentage: totalAllocation > 0 ? Math.round((distribution.SENIOR.allocation / totalAllocation) * 100) : 0
      },
      total: {
        count: totalResources,
        allocation: Math.round(totalAllocation)
      }
    };
  };

  // Calculate average experience for a project
  const calculateAverageExperience = (resources) => {
    if (!resources || resources.length === 0) return 0;
    
    const totalExperience = resources.reduce((sum, resource) => {
      return sum + (parseInt(resource.totalExperience) || 0);
    }, 0);
    
    const avgMonths = totalExperience / resources.length;
    return Math.round((avgMonths / 12) * 10) / 10; // Convert to years with 1 decimal
  };



  // Process project allocation data into summary
  useEffect(() => {
    if (!projectAllocationData || !projectAllocationData.data) return;

    // Group data by project
    const projectGroups = {};
    
    projectAllocationData.data.forEach(allocation => {
      const projectName = allocation.projectName;
      if (!projectGroups[projectName]) {
        projectGroups[projectName] = {
          projectName,
          clientName: allocation.clientName,
          projectManagerName: allocation.projectManagerName,
          resources: []
        };
      }
      projectGroups[projectName].resources.push(allocation);
    });

    // Calculate summary for each project
    const summary = Object.values(projectGroups).map(project => {
      const experienceDistribution = calculateExperienceDistribution(project.resources);
      const profitabilityScore = calculateProfitabilityScore(project.resources);
      const profitabilityInfo = getProfitabilityCategory(profitabilityScore);
      const avgExperience = calculateAverageExperience(project.resources);

      // Calculate billable percentage
      const billableResources = project.resources.filter(r => 
        r.billStatus && r.billStatus.toLowerCase().includes('billable') && !r.billStatus.toLowerCase().includes('non')
      );
      const billablePercentage = project.resources.length > 0 
        ? Math.round((billableResources.length / project.resources.length) * 100) 
        : 0;

      return {
        projectName: project.projectName,
        clientName: project.clientName,
        projectManagerName: project.projectManagerName,
        totalResources: project.resources.length,
        experienceDistribution,
        profitabilityScore,
        profitabilityCategory: profitabilityInfo.category,
        profitabilityColor: profitabilityInfo.color,
        profitabilityTrend: profitabilityInfo.trend,
        averageExperience: avgExperience,
        billablePercentage
      };
    });

    setSummaryData(summary);
  }, [projectAllocationData]);

  // Sorting logic
  const sortedSummaryData = useMemo(() => {
    return [...summaryData].sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      // Handle nested properties
      if (sortField === 'juniorPercentage') {
        aValue = a.experienceDistribution.junior.percentage;
        bValue = b.experienceDistribution.junior.percentage;
      } else if (sortField === 'seniorPercentage') {
        aValue = a.experienceDistribution.senior.percentage;
        bValue = b.experienceDistribution.senior.percentage;
      }

      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  }, [summaryData, sortField, sortDirection]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIndicator = ({ field }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? ' ▲' : ' ▼';
  };

  const ExperienceBar = ({ distribution }) => {
    const { junior, mid, senior } = distribution;
    const total = junior.percentage + mid.percentage + senior.percentage;
    
    if (total === 0) return <div className="experience-bar empty">No data</div>;

    return (
      <div className="experience-bar">
        <div className="bar-container">
          <div 
            className="bar-segment junior" 
            style={{ 
              width: `${junior.percentage}%`,
              backgroundColor: EXPERIENCE_LEVELS.JUNIOR.color 
            }}
            title={`Junior: ${junior.count} resources (${junior.percentage}%)`}
          />
          <div 
            className="bar-segment mid" 
            style={{ 
              width: `${mid.percentage}%`,
              backgroundColor: EXPERIENCE_LEVELS.MID.color 
            }}
            title={`Mid: ${mid.count} resources (${mid.percentage}%)`}
          />
          <div 
            className="bar-segment senior" 
            style={{ 
              width: `${senior.percentage}%`,
              backgroundColor: EXPERIENCE_LEVELS.SENIOR.color 
            }}
            title={`Senior: ${senior.count} resources (${senior.percentage}%)`}
          />
        </div>
        <div className="bar-labels">
          <span className="junior-label">{junior.count}J</span>
          <span className="mid-label">{mid.count}M</span>
          <span className="senior-label">{senior.count}S</span>
        </div>
      </div>
    );
  };

  if (!summaryData.length) {
    return (
      <div className="project-summary-container">
        <div className="no-data">No project data available</div>
      </div>
    );
  }

  return (
    <div className="project-summary-container">
      <div className="summary-header">
        <h2>Project Summary - Resource Mix & Profitability Analysis</h2>
        <div className="legend">
          <div className="legend-item">
            <span className="legend-color junior"></span>
            <span>Junior (0-2 yrs) - High Profit Potential</span>
          </div>
          <div className="legend-item">
            <span className="legend-color mid"></span>
            <span>Mid (2-5 yrs) - Balanced Cost</span>
          </div>
          <div className="legend-item">
            <span className="legend-color senior"></span>
            <span>Senior (5+ yrs) - Higher Cost</span>
          </div>
        </div>
      </div>

      <div className="summary-table-container">
        <table className="project-summary-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('projectName')} className="sortable">
                Project Name <SortIndicator field="projectName" />
              </th>
              <th onClick={() => handleSort('clientName')} className="sortable">
                Client <SortIndicator field="clientName" />
              </th>
              <th onClick={() => handleSort('totalResources')} className="sortable">
                Resources <SortIndicator field="totalResources" />
              </th>
              <th>Experience Mix</th>
              <th onClick={() => handleSort('averageExperience')} className="sortable">
                Avg Exp <SortIndicator field="averageExperience" />
              </th>
              <th onClick={() => handleSort('profitabilityScore')} className="sortable">
                Profitability Score <SortIndicator field="profitabilityScore" />
              </th>
              <th onClick={() => handleSort('billablePercentage')} className="sortable">
                Billable % <SortIndicator field="billablePercentage" />
              </th>
              <th onClick={() => handleSort('projectManagerName')} className="sortable">
                PM <SortIndicator field="projectManagerName" />
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedSummaryData.map((project, index) => (
              <tr key={index} className="project-row">
                <td className="project-name">
                  <strong>{project.projectName}</strong>
                </td>
                <td className="client-name">{project.clientName}</td>
                <td className="total-resources">
                  <span className="resource-count">{project.totalResources}</span>
                </td>
                <td className="experience-mix">
                  <ExperienceBar distribution={project.experienceDistribution} />
                  <div className="mix-details">
                    J: {project.experienceDistribution.junior.count} |{' '}
                    M: {project.experienceDistribution.mid.count} |{' '}
                    S: {project.experienceDistribution.senior.count}
                  </div>
                </td>
                <td className="avg-experience">
                  <span className="experience-value">{project.averageExperience} yrs</span>
                </td>
                <td className="profitability">
                  <div className="profitability-container">
                    <span 
                      className="profitability-score"
                      style={{ color: project.profitabilityColor }}
                    >
                      {project.profitabilityScore}
                    </span>
                    <span 
                      className="profitability-trend"
                      style={{ color: project.profitabilityColor }}
                    >
                      {project.profitabilityTrend}
                    </span>
                  </div>
                  <div 
                    className="profitability-category"
                    style={{ color: project.profitabilityColor }}
                  >
                    {project.profitabilityCategory}
                  </div>
                </td>
                <td className="billable-percentage">
                  <span className={`billable-value ${project.billablePercentage >= 80 ? 'high' : project.billablePercentage >= 60 ? 'medium' : 'low'}`}>
                    {project.billablePercentage}%
                  </span>
                </td>
                <td className="project-manager">{project.projectManagerName}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="summary-insights">
        <div className="insights-header">
          <h3>Key Insights</h3>
        </div>
        <div className="insights-grid">
          <div className="insight-card">
            <h4>High Profitability Projects</h4>
            <div className="insight-count">
              {sortedSummaryData.filter(p => p.profitabilityCategory === 'High').length}
            </div>
            <p>Projects with junior-heavy teams</p>
          </div>
          <div className="insight-card">
            <h4>Senior-Heavy Projects</h4>
            <div className="insight-count">
              {sortedSummaryData.filter(p => p.experienceDistribution.senior.percentage > 50).length}
            </div>
            <p>Projects with high delivery capability</p>
          </div>
          <div className="insight-card">
            <h4>Balanced Teams</h4>
            <div className="insight-count">
              {sortedSummaryData.filter(p => p.profitabilityCategory === 'Moderate').length}
            </div>
            <p>Projects with optimal resource mix</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectSummaryDetails;
